import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PublishedScreen, SCREEN_ASSETS, SCREEN_SLUG, RESERVED_SLUGS, ScreenCatalogEntry, readPublishedScreen } from '../definitions/published-screen.model';
import { ModuleFlagsService } from '@shared-lib/foundry-shared/module-flags.service';
import type { EditorDocument } from '../../modules/design-builder/design-editor-state';
import type { LayoutItem } from '../layout/layout.model';

/** Storage boundary: replace this transport when moving definitions to GitHub or an API. */
@Injectable({ providedIn: 'root' })
export class PublishedScreenService {
  private readonly http = inject(HttpClient);
  private readonly flags = inject(ModuleFlagsService);
  readonly catalog = signal<ScreenCatalogEntry[]>([]);
  private remember(screen:PublishedScreen): void {
    this.catalog.update(screens => [...screens.filter(item => item.slug !== screen.slug), {slug:screen.slug,title:screen.title,accessKey:screen.accessKey,icon:screen.icon}]);
  }
  /** Read-time upgrade of existing browser drafts/imports; there is no legacy rendering path. */
  async migrate(document: EditorDocument): Promise<EditorDocument> {
    const result = structuredClone(document);
    for (const page of result.pages) {
      if (!page.source) continue;
      const legacy = page.source;
      const slug = legacy.screenId === 'item-catalogs' ? 'domain-catalogs' : legacy.screenId;
      const published = await this.load(slug);
      const base = structuredClone(published.document);
      const pageTemplate = base.pages.find(item => item.id === base.activePageId)!;
      const rename = new Map(pageTemplate.nodes.map(node => [node.id, `${page.id}-${node.id}`]));
      for (const feature of base.features ?? []) {
        const oldId = feature.id; feature.id = `${page.id}-${feature.id}`; feature.config = structuredClone(legacy.config);
        for (const node of pageTemplate.nodes) if (node.componentId?.startsWith(`view:${oldId}/`)) node.componentId = node.componentId.replace(`view:${oldId}/`, `view:${feature.id}/`);
        (result.features ??= []).push(feature);
      }
      for (const node of pageTemplate.nodes) {
        node.id = rename.get(node.id)!;
        if (!base.features?.length && node.configKey) node.config = structuredClone((legacy.config['components'] as Record<string, Record<string,unknown>>)?.[node.configKey] ?? node.config ?? {});
      }
      const layout = (item: LayoutItem): LayoutItem => ({ ...item, id:`${page.id}-${item.id}`, nodeId:item.nodeId?rename.get(item.nodeId):undefined, children:item.children?.map(layout) });
      page.nodes = [...pageTemplate.nodes, ...page.nodes];
      page.layout = pageTemplate.layout ? layout(pageTemplate.layout) : undefined;
      if (page.layout) for (const node of page.nodes.filter(node => !rename.has(node.id) && !pageTemplate.nodes.includes(node))) (page.layout.children ??= []).push({ id:`placement-${node.id}`,nodeId:node.id,styles:{position:'absolute',left:`${node.x}px`,top:`${node.y}px`,width:`${node.width}px`,height:`${node.height}px`} });
      delete page.source;
    }
    return result;
  }
  async refresh(): Promise<ScreenCatalogEntry[]> {
    await this.flags.load();
    // Existing navigation supplies directory suggestions. Direct URLs do not
    // depend on this list or on a separate screen manifest.
    const slugs = [...new Set([...this.flags.getAvailableScreenIds(), ...this.catalog().map(screen => screen.slug)])];
    await Promise.allSettled(slugs.filter(slug => SCREEN_SLUG.test(slug) && !RESERVED_SLUGS.has(slug)).map(slug => this.load(slug)));
    return this.catalog();
  }
  async load(slug: string): Promise<PublishedScreen> {
    if (!SCREEN_SLUG.test(slug) || slug.length > 80 || RESERVED_SLUGS.has(slug)) throw new Error('Invalid screen URL.');
    await this.flags.load();
    const flag = this.flags.getScreenConfig(slug);
    const paths = this.assetPaths(slug, flag);
    for (const [index, path] of paths.entries()) {
      let value: unknown;
      try {
        value = await firstValueFrom(this.http.get<unknown>(path));
      } catch (error) {
        // A missing file may live in the other folder. Invalid JSON, denied
        // requests and transport failures must remain visible to the caller.
        if (error instanceof HttpErrorResponse && error.status === 404 && index < paths.length - 1) continue;
        throw error;
      }
      const name = (value as Partial<EditorDocument> | null)?.name;
      const screen = readPublishedScreen(value, {slug,title:flag?.label ?? (typeof name === 'string' ? name : slug),accessKey:slug,icon:'dashboard'});
      this.remember(screen);
      return screen;
    }
    throw new Error('Screen unavailable.');
  }
  private assetPaths(slug: string, flag?: { custom?: boolean; configFile?: string }): string[] {
    const configured = flag?.configFile?.replace(/\\/g, '/').replace(/^\/+/, '');
    // Only local screen assets are supported; module configuration cannot
    // redirect a screen request to an external URL or traverse directories.
    if (configured && !/^(?:assets\/(?:custom|foundry-screens)\/)?(?:[a-zA-Z0-9_-]+\/)*[a-zA-Z0-9_-]+\.json$/.test(configured)) {
      throw new Error('Invalid screen configuration path.');
    }
    const root = configured && Object.values(SCREEN_ASSETS).find(folder => configured.startsWith(folder));
    const relative = configured ? (root ? configured.slice(root.length) : configured) : `${slug}.json`;
    const preferred = flag?.custom ? SCREEN_ASSETS.custom + relative : configured && root ? configured : SCREEN_ASSETS.foundry + relative;
    const fallback = preferred.startsWith(SCREEN_ASSETS.custom) ? SCREEN_ASSETS.foundry + relative : SCREEN_ASSETS.custom + relative;
    return [...new Set([preferred, fallback])];
  }
}
